import React, { useCallback } from 'react';
import _indexOf from 'lodash/indexOf';
import classNames from 'classnames';
import {
  Tile, TileText, TileContent, Button, useBreakpoint, Badge
} from '@one-thd/sui-atomic-components';
import {
  useDropZoneStore, useDigitalDataStore
} from '../../store';
import { useDesignStore } from '../../store/design/context';
import { Draggable } from '../../components/Draggable';
import logNewRelicCustom from '../../utils/logNewRelicCustom';
import ChoicePrice from './components/ChoicePrice';
import ChoiceQuantity from './components/ChoiceQuantity';
import ChoiceImage from './components/ChoiceImage';
import { choiceDefaultProps, choiceProps } from './_types';
import { ACTIVE_GHOST_BTN_OVERRIDE, INACTIVE_GHOST_BTN_OVERRIDE } from './_styles';
import { NEW_RELIC_EVENTS } from '../../constants';

const Choice = ({
  choice: {
    id,
    mediaUrl,
    shortLabel,
    sku,
    inExcludedShipState,
    promoType,
    disabled,
    isAvailable
  },
  quickViewHandler,
  choiceMetaData,
  product
}) => {
  const selectProduct = useDesignStore((state) => state.select);
  const resetDropZones = useDropZoneStore((state) => state.resetDropZones);
  const trackQuickViewModalLoadEvent = useDigitalDataStore((state) => state.trackQuickViewModalLoadEvent);
  const selectedProduct = useDesignStore((state) => state.design.selectedProduct);

  const matches = useBreakpoint('lg');
  const isDesktop = matches.greater;

  const handleProductClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    selectProduct({ metadata: choiceMetaData, choiceId: id }, resetDropZones);
    logNewRelicCustom(NEW_RELIC_EVENTS.CLICK_START_CHOICE, { choiceId: id });
  }, []);

  const onClickQuickView = (itemId) => {
    trackQuickViewModalLoadEvent();
    quickViewHandler({ itemId });
    logNewRelicCustom(NEW_RELIC_EVENTS.QUICK_VIEW, { itemId });
  };

  const getStyle = (style) => {
    return isDesktop ? style.desktop : style.mobile;
  };

  const tileStyle = {
    desktop: {
      height: 250,
      // width: 150,
      alignItems: 'flex-start',
      border: `2px solid ${selectedProduct?.itemId === sku ? '#f96302' : '#ccc'}`,
      borderRadius: '3px 3px 0px 0px'
    },
    mobile: {
      height: 205,
      // width: 120,
      alignItems: 'flex-start',
      border: `2px solid ${selectedProduct?.itemId === sku ? '#f96302' : '#ccc'}`,
      borderRadius: '3px 3px 0px 0px'
    }
  };

  const responsiveTileStyle = getStyle(tileStyle);

  const buttonStyle = {
    desktop: {
      color: !product ? '#ccc' : '#3E7697',
      border: '2px solid #ccc',
      borderTop: 'none',
      height: 44,
      width: '100%',
      borderRadius: '0px 0px 3px 3px'
    },
    mobile: {
      color: '#3E7697',
      border: '2px solid #ccc',
      borderTop: 'none',
      height: 30,
      width: '100%',
      borderRadius: '0px 0px 3px 3px'
    }
  };

  const responsiveButtonStyle = getStyle(buttonStyle);

  const imageStyle = {
    desktop: {
      height: 100,
      width: 100,
    },
    mobile: {
      height: 65,
      width: 65,
    }
  };

  const responsiveImageStyle = getStyle(imageStyle);

  const tileFlexStyle = {
    desktop: {
      display: 'flex',
      flexDirection: 'column',
    },
    mobile: {
      display: 'flex',
      flexDirection: 'column-reverse',
    }
  };

  const responsiveTileFlexStyle = getStyle(tileFlexStyle);

  return id && (
    <>
      <div style={responsiveTileFlexStyle}>
        <div
          className="sui-flex sui-flex-col choice"
          role="button"
          tabIndex={0}
          data-choice-id={id}
          data-sku={sku}
          data-grid-span={choiceMetaData?.GridSpan}
        >
          <Tile
            component="div"
            value={sku}
            style={responsiveTileStyle}
            onMouseDown={(event) => handleProductClick(event)}
            onTouchStart={(event) => handleProductClick(event)}
            aria-label={`choice ${shortLabel}`}
            disabled={disabled}
          >
            <TileContent
              style={{ alignSelf: 'stretch', justifyContent: 'space-between', width: '100%' }}
              orientation="vertical"
              alignItems="start"
            >
              <div
                style={{
                  height: responsiveImageStyle.height,
                }}
                className="sui-flex sui-items-center sui-flex-col sui-self-center"
              >
                {product?.fulfillment && (
                  <>
                    {(!isAvailable && !inExcludedShipState) && (
                      <span className="sui-absolute custom-badge">
                        <Badge size="small" variant="regular" color="danger">Out of Stock</Badge>
                      </span>
                    )}
                    {(!isAvailable && inExcludedShipState) && (
                      <span className="sui-absolute custom-badge">
                        <Badge size="small" variant="regular" color="danger">Unavailable</Badge>
                      </span>
                    )}
                  </>
                )}
                <Draggable isVisualizerDrag={false} id={id} choiceId={id}>
                  <ChoiceImage
                    disabled={disabled}
                    mediaUrl={mediaUrl}
                  />
                </Draggable>
              </div>
              <TileText align="left" lineClamp="3" height="normal" style={{ maxHeight: '6rem' }}>{shortLabel}</TileText>
              <ChoicePrice
                graphProduct={product}
                promoType={promoType}
                sku={sku}
              />
              <ChoiceQuantity choiceId={id} />
            </TileContent>
          </Tile>
          <Button
            style={responsiveButtonStyle}
            className={
              classNames({
                INACTIVE_GHOST_BTN_OVERRIDE: !product,
                ACTIVE_GHOST_BTN_OVERRIDE: product
              })
            }
            onClick={
              !product
                ? () => null
                : () => onClickQuickView(sku)
            }
            variant="ghost"
            value={id}
            disabled={!product}
          >
            Quick View
          </Button>
        </div>
      </div>
    </>
  );
};

Choice.propTypes = choiceProps;

Choice.defaultProps = choiceDefaultProps;

export default Choice;
